import React, { createContext, useContext } from 'react';
import { Button, Chip, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { formatDateLocale, parseScore } from '../utils/dateutils';
import { formatCurrencyV2, isNumericFieldValue } from './actionOutputV2Utils';
import { MarkdownContent } from './AISummaryAccordion';
import { PredictionExplainerInfoIcon } from './PredictionExplainer';

export type ScoreColorName = 'success' | 'warning' | 'danger' | 'default';

/**
 * Score → color thresholds. `greenMin`/`yellowMin` are the inclusive lower bounds for
 * the green (success) and yellow (warning) bands; anything below `yellowMin` is red (danger).
 * These historically lived hardcoded in the UI (85 / 60) and are now driven by the agent
 * configuration (`workflow_data.score_color_thresholds`), falling back to these defaults.
 */
export interface ScoreThresholds {
    greenMin: number;
    yellowMin: number;
}

export const DEFAULT_SCORE_THRESHOLDS: ScoreThresholds = { greenMin: 85, yellowMin: 60 };

/** Uses `green_min` / `yellow_min` when both are numbers. Otherwise returns 85 / 60. */
export const resolveScoreThresholds = (raw: unknown): ScoreThresholds => {
    if (!raw || typeof raw !== 'object') return DEFAULT_SCORE_THRESHOLDS;
    const cfg = raw as Record<string, unknown>;
    const greenMin = Number(cfg.green_min);
    const yellowMin = Number(cfg.yellow_min);
    if (!Number.isFinite(greenMin) || !Number.isFinite(yellowMin)) return DEFAULT_SCORE_THRESHOLDS;
    return { greenMin, yellowMin };
};

export const getScoreColor = (
    score: unknown,
    thresholds: ScoreThresholds = DEFAULT_SCORE_THRESHOLDS
): 'success' | 'warning' | 'danger' => {
    const numScore = Math.round(parseScore(score as number | string));
    if (numScore >= thresholds.greenMin) return 'success';
    if (numScore >= thresholds.yellowMin) return 'warning';
    return 'danger';
};

/**
 * Context carrying the active score thresholds for the subtree. Defaults to the historical
 * 85 / 60 values so any consumer rendered outside a provider behaves exactly as before.
 */
const ScoreThresholdsContext = createContext<ScoreThresholds>(DEFAULT_SCORE_THRESHOLDS);

export const ScoreThresholdsProvider = ScoreThresholdsContext.Provider;

export const useScoreThresholds = (): ScoreThresholds => useContext(ScoreThresholdsContext);

/** `getScoreColor` bound to the thresholds from context. */
export const useScoreColor = () => {
    const thresholds = useContext(ScoreThresholdsContext);
    return (score: unknown): 'success' | 'warning' | 'danger' => getScoreColor(score, thresholds);
};

/** `renderScoreChip` bound to the thresholds from context. */
export const useRenderScoreChip = () => {
    const thresholds = useContext(ScoreThresholdsContext);
    return (score: unknown, explicitColor?: ScoreColorName | null) => renderScoreChip(score, explicitColor, thresholds);
};

export const getScoreColorFromFormat = (format: string | null | undefined): ScoreColorName | null => {
    if (format === 'success' || format === 'warning' || format === 'danger') return format;
    return null;
};

export const parseAndFormatScore = (score: unknown): { parsedScore: number | null; scoreDisplay: string } => {
    const raw = score !== undefined && score !== null ? parseScore(score as number | string) : null;
    const isInvalid = raw === null || Number.isNaN(raw);
    return {
        parsedScore: isInvalid ? null : raw,
        scoreDisplay: isInvalid ? '--' : `${Math.round(raw)}%`,
    };
};

export const ActionScoreDisplay: React.FC<{
    parsedScore: number | null;
    scoreColor: string;
    scoreDisplay: string;
}> = ({ parsedScore, scoreColor, scoreDisplay }) => {
    if (parsedScore === null) {
        return null;
    }

    const chipColor = ['success', 'warning', 'danger'].includes(scoreColor)
        ? (scoreColor as 'success' | 'warning' | 'danger')
        : 'default';

    return (
        <Chip color={chipColor} variant="flat" size="sm">
            {scoreDisplay}
        </Chip>
    );
};

export const renderScoreChip = (
    score: unknown,
    explicitColor?: ScoreColorName | null,
    thresholds: ScoreThresholds = DEFAULT_SCORE_THRESHOLDS
) => {
    const parsedScore = typeof score === 'string' ? Number.parseFloat(score) : score;
    const isNumeric = typeof parsedScore === 'number' && !Number.isNaN(parsedScore);
    let displayValue: string;
    if (isNumeric) {
        displayValue = `${Math.round(parsedScore as number)}%`;
    } else if (typeof score === 'string' || typeof score === 'number') {
        displayValue = String(score);
    } else {
        displayValue = '--';
    }
    const chipColor: ScoreColorName = explicitColor ?? (isNumeric ? getScoreColor(parsedScore, thresholds) : 'default');
    return (
        <Chip color={chipColor} variant="flat" size="sm">
            {displayValue}
        </Chip>
    );
};

/** Score chip that reads agent-config thresholds from context. An explicit color still wins. */
export const ContextScoreChip: React.FC<{
    score: unknown;
    explicitColor?: ScoreColorName | null;
}> = ({ score, explicitColor = null }) => {
    const renderChip = useRenderScoreChip();
    return renderChip(score, explicitColor);
};

export const ACCORDION_TITLE_TEXT_CLASS = 'min-w-0 break-all [overflow-wrap:anywhere]';

export const LINE_ACCORDION_CLASS_NAMES = {
    content: 'pl-0 pr-0 overflow-hidden min-h-0',
    base: 'w-full max-w-full min-w-0 !border-none !shadow-none !bg-transparent !px-0 min-h-0 overflow-x-hidden',
    title: 'text-base w-full min-w-0 max-w-full overflow-hidden whitespace-normal',
    trigger: 'group py-2 min-w-0 max-w-full w-full overflow-x-hidden items-center',
} as const;

export const BOX_ACCORDION_CLASS_NAMES = {
    content: 'pl-1 pr-0 overflow-hidden min-h-0',
    base: 'w-full max-w-full min-w-0 !px-2 !shadow-none border-2 min-h-0 overflow-x-hidden',
    title: 'text-sm min-w-0 flex-1 max-w-full overflow-hidden whitespace-normal',
    trigger: 'min-w-0 max-w-full w-full overflow-x-hidden gap-2 items-center',
    indicator: 'shrink-0 flex-none',
} as const;

/** Nested light accordions (box children) — hide dividers; V2 adds flush horizontal padding. */
const NESTED_LIGHT_ACCORDION_CLASS =
    'gap-3 [&_hr]:hidden [&_.divider]:hidden [&_[role=separator]]:hidden min-h-0 [&_[data-slot=content]]:overflow-hidden';

export const V2_NESTED_LIGHT_ACCORDION_CLASS = `${NESTED_LIGHT_ACCORDION_CLASS} !px-0 overflow-x-hidden [&_[data-slot=trigger]]:min-w-0 [&_[data-slot=trigger]]:max-w-full [&_[data-slot=trigger]]:overflow-x-hidden [&_[data-slot=title]]:min-w-0 [&_[data-slot=title]]:overflow-hidden`;

export const HIDDEN_ACCORDION_DIVIDER_PROPS = { className: 'hidden' } as const;

export type HighlightColorName = 'warning' | 'success' | 'danger';

export const getHighlightColorFromFormat = (format: string | null | undefined): HighlightColorName | null => {
    if (format === 'highlight' || format === 'highlight-warning') return 'warning';
    if (format === 'highlight-success') return 'success';
    if (format === 'highlight-danger') return 'danger';
    return null;
};

const HIGHLIGHT_COLOR_CLASSES: Record<HighlightColorName, { container: string; label: string; text: string }> = {
    warning: {
        container: 'border-warning-400 bg-warning-50 dark:bg-warning-500/10',
        label: 'text-warning-800 dark:text-warning-300',
        text: 'text-warning-700 dark:text-warning-300',
    },
    success: {
        container: 'border-success-400 bg-success-50 dark:bg-success-500/10',
        label: 'text-success-800 dark:text-success-300',
        text: 'text-success-700 dark:text-success-300',
    },
    danger: {
        container: 'border-danger-400 bg-danger-50 dark:bg-danger-500/10',
        label: 'text-danger-800 dark:text-danger-300',
        text: 'text-danger-700 dark:text-danger-300',
    },
};

/** Colored callout for description / @highlight fields (legacy + V2). Defaults to warning. */
export const ClaimScoreHighlightField: React.FC<{
    fieldKey?: string;
    label: string;
    value: string;
    color?: HighlightColorName;
}> = ({ fieldKey, label, value, color = 'warning' }) => {
    const classes = HIGHLIGHT_COLOR_CLASSES[color];
    return (
        <div key={fieldKey} className={`mt-3 p-3 rounded-lg border-l-4 ${classes.container}`}>
            <div className="flex items-start">
                <div>
                    <span className={`text-xs font-medium uppercase tracking-wide ${classes.label}`}>{label}</span>
                    <p className={`text-sm mt-1 whitespace-pre-wrap ${classes.text}`}>{value}</p>
                </div>
            </div>
        </div>
    );
};

const UNIT_SUFFIX_MAP: Record<string, string> = {
    hours: ' hrs',
    miles: ' mi',
    mm: ' mm',
    percentage: '%',
};

const isValidDateString = (value: unknown): value is string =>
    typeof value === 'string' && !!value.trim() && !Number.isNaN(new Date(`${value}T00:00:00`).getTime());

const ClaimScoreKeyValue: React.FC<{
    fieldKey: string;
    label: string;
    valueClassName?: string;
    children: React.ReactNode;
}> = ({ fieldKey, label, valueClassName = 'renderer-field-value text-default-500 mb-1', children }) => (
    <div key={fieldKey} id={fieldKey} className="renderer-key-value-block">
        <div className="renderer-field-name capitalize font-medium text-default-700 mb-1">{label}</div>
        <div className={valueClassName}>{children}</div>
    </div>
);

/**
 * Leaf `@suffix` formatting shared by the claim score panel and the source JSON modal.
 * Table, collapse, and attachment stay in the panel renderer.
 */
export function renderClaimScoreFormattedField(
    fieldKey: string,
    label: string,
    format: string,
    value: unknown
): React.ReactNode | null {
    if (format === 'link' && typeof value === 'string' && value.trim()) {
        return <ClaimScoreLinkField fieldKey={fieldKey} label={label} url={value} />;
    }
    if (format === 'links') {
        const urls = Array.isArray(value)
            ? value.filter((item): item is string => typeof item === 'string' && item.trim() !== '').map((item) => item.trim())
            : [];
        if (urls.length === 0) return null;
        return <ClaimScoreLinksField fieldKey={fieldKey} label={label} urls={urls} />;
    }
    if (format === 'explainer' && typeof value === 'string' && value.trim()) {
        return <ClaimScoreExplainerField fieldKey={fieldKey} label={label} messageId={value} />;
    }
    const highlightColor = getHighlightColorFromFormat(format);
    if (highlightColor && typeof value === 'string' && value.trim()) {
        return <ClaimScoreHighlightField fieldKey={fieldKey} label={label} value={value} color={highlightColor} />;
    }
    if (format === 'markdown' && typeof value === 'string' && value.trim()) {
        return (
            <ClaimScoreKeyValue fieldKey={fieldKey} label={label} valueClassName="renderer-field-value">
                <MarkdownContent content={value} />
            </ClaimScoreKeyValue>
        );
    }
    if (format === 'currency' && isNumericFieldValue(value, true)) {
        return <ClaimScoreKeyValue fieldKey={fieldKey} label={label}>{formatCurrencyV2(Number(value))}</ClaimScoreKeyValue>;
    }
    if (format === 'date' && isValidDateString(value)) {
        return <ClaimScoreKeyValue fieldKey={fieldKey} label={label}>{formatDateLocale(value)}</ClaimScoreKeyValue>;
    }
    const scoreColor = getScoreColorFromFormat(format);
    const isScoreValue = isNumericFieldValue(value) || (typeof value === 'string' && value.trim() !== '');
    if ((format === 'score' || scoreColor) && isScoreValue) {
        return (
            <div key={fieldKey} id={fieldKey} className="flex items-center justify-between gap-2 py-1 mb-1">
                <span className="renderer-field-name capitalize font-medium text-default-700">{label}</span>
                <ContextScoreChip score={value} explicitColor={scoreColor} />
            </div>
        );
    }
    const unit = UNIT_SUFFIX_MAP[format];
    if (unit && isNumericFieldValue(value)) {
        return <ClaimScoreKeyValue fieldKey={fieldKey} label={label}>{`${String(value)}${unit}`}</ClaimScoreKeyValue>;
    }
    return null;
}

const ClaimScoreLinkField: React.FC<{ fieldKey: string; label: string; url: string }> = ({ fieldKey, label, url }) => {
    const { t } = useTranslation();
    return (
        <div key={fieldKey} className="renderer-key-value-block mb-1">
            <div className="flex items-center gap-1">
                <span className="renderer-field-name capitalize font-medium text-default-700">{label}</span>
                <Tooltip content={t('Open link')} placement="top">
                    <Button
                        isIconOnly
                        variant="light"
                        size="sm"
                        className="min-w-8 w-8 h-8"
                        aria-label={t('Open link')}
                        onPress={() => window.open(url, '_blank', 'noopener,noreferrer')}
                    >
                        <Icon icon="mdi:open-in-new" className="w-4 h-4 text-primary" />
                    </Button>
                </Tooltip>
            </div>
        </div>
    );
};

const TruncatedUrlLink: React.FC<{ url: string }> = ({ url }) => {
    const linkRef = React.useRef<HTMLAnchorElement>(null);
    const [isTruncated, setIsTruncated] = React.useState(false);

    React.useLayoutEffect(() => {
        const el = linkRef.current;
        if (!el) return;
        const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [url]);

    return (
        <Tooltip content={url} placement="top" isDisabled={!isTruncated}>
            <a
                ref={linkRef}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block max-w-full truncate text-left text-sm text-primary hover:underline"
            >
                [{url}]
            </a>
        </Tooltip>
    );
};

const ClaimScoreLinksField: React.FC<{ fieldKey: string; label: string; urls: string[] }> = ({ fieldKey, label, urls }) => (
    <div key={fieldKey} className="renderer-key-value-block mb-1 min-w-0">
        <div className="renderer-field-name capitalize font-medium text-default-700 mb-1">{label}</div>
        <div className="space-y-1 min-w-0">
            {urls.map((url, index) => (
                <TruncatedUrlLink key={`${url}-${index}`} url={url} />
            ))}
        </div>
    </div>
);

const ClaimScoreExplainerField: React.FC<{ fieldKey: string; label: string; messageId: string }> = ({
    fieldKey,
    label,
    messageId,
}) => {
    const { t } = useTranslation();
    return (
        <div key={fieldKey} className="renderer-key-value-block mb-1">
            <div className="flex items-center gap-2">
                <span className="renderer-field-name capitalize font-medium text-default-700">{label}</span>
                <Tooltip content={t('Click to view prediction explainer')} placement="top" showArrow>
                    <span className="inline-flex">
                        <PredictionExplainerInfoIcon messageId={messageId} />
                    </span>
                </Tooltip>
            </div>
        </div>
    );
};
